import React from 'react';
import { Sparkles, ChevronRight } from 'lucide-react';
import { cn } from './lib/utils';
import { ICON_PX } from './lib/iconSizes';
import { AlertRow } from './AlertRow';

/**
 * Combined brief card — narrative + alerts + insights in one neutral container.
 *
 * Used as the top-of-page summary on Overview tabs. Renders up to three
 * sections (narrative, alerts, insights), each independently optional.
 * Sections are separated by extra spacing (space-y-4).
 *
 * <BriefCard
 *   narrative="You have 18 tasks today. 2 are already overdue."
 *   alerts={[
 *     { id: 'a1', text: 'Fire Drill 0% complete', onClick: () => {} },
 *   ]}
 *   insights={[
 *     { id: 'i1', text: 'Maria has 3 overdue tasks', onClick: () => {} },
 *   ]}
 * />
 */
function BriefCard({ narrative, alerts = [], insights = [], className }) {
  const hasContent = narrative || alerts.length > 0 || insights.length > 0;
  if (!hasContent) return null;

  return (
    <div className={cn('rounded-lg border border-nn-border bg-nn-white p-3 space-y-4', className)}>
      {/* Narrative */}
      {narrative && (
        <div className="flex items-start gap-2.5">
          <Sparkles size={ICON_PX.md} className="text-aqua shrink-0 mt-0.5" />
          <p className="text-body-sm text-text-dark flex-1">{narrative}</p>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-1.5">
          {alerts.map((alert) => (
            <AlertRow key={alert.id} onClick={alert.onClick}>
              {alert.text}
            </AlertRow>
          ))}
        </div>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <div className="space-y-1.5">
          {insights.map((insight) => (
            <div
              key={insight.id}
              role="button"
              tabIndex={0}
              className="rounded-md bg-aqua-glow/10 px-3 py-2 flex items-center gap-2.5 cursor-pointer hover:bg-aqua-glow/20 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-aqua/50"
              onClick={insight.onClick}
              onKeyDown={(e) => e.key === 'Enter' && insight.onClick?.()}
            >
              <Sparkles size={ICON_PX.md} className="text-aqua shrink-0" />
              <span className="text-body-sm text-text-dark flex-1">{insight.text}</span>
              <ChevronRight size={ICON_PX.sm} className="text-aqua shrink-0" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { BriefCard };
